/*
 * Copyright (c) 2025, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import { observer } from "mobx-react-lite"

import type { Organization } from "../../features/organization/organization-types"
import { OrganizationViewModel } from "../../features/organization/organization-viewmodel"
import { useViewModel } from "../app/app-hooks"
import { Card } from "../primitives/card"
import { Skeleton } from "../primitives/skeleton"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../primitives/table"
import { CreateOrganization } from "./create-organization"

export const AllOrganizationsView = observer(() => {
    const vm = useViewModel(OrganizationViewModel)
    const isLoading = !vm.organizationsLoaded

    return (
        <div className="w-content">
            <div className="max-w-175">
                <div className="mb-6 flex items-center justify-between">
                    <h1 className="m-0 text-3xl font-bold text-primary">All Organizations</h1>
                    <CreateOrganization />
                </div>

                <Card className="py-0">
                    {/* No row hover: the rows are not clickable, so the highlight promises nothing. */}
                    <Table aria-busy={isLoading} className="[&_tr]:hover:bg-transparent">
                        <colgroup>
                            <col className="w-1/10" />
                            <col />
                        </colgroup>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Org ID</TableHead>
                                <TableHead>Name</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {isLoading
                                ? renderSkeletons()
                                : vm.hasOrganizations
                                  ? vm.organizations.map(renderOrganization)
                                  : renderEmptyRow()}
                        </TableBody>
                    </Table>
                </Card>
            </div>
        </div>
    )
})

function renderOrganization(org: Organization) {
    return (
        <TableRow key={org.id}>
            <TableCell className="tabular-nums">#{org.id}</TableCell>
            <TableCell>{org.name}</TableCell>
        </TableRow>
    )
}

function renderEmptyRow() {
    return (
        <TableRow>
            <TableCell colSpan={2} className="h-24 text-center text-muted-foreground">
                No organizations
            </TableCell>
        </TableRow>
    )
}

function renderSkeletons() {
    return Array.from({ length: 10 }, (_, i) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: the rows are identical and never reorder
        <TableRow key={i} aria-hidden>
            <TableCell>
                <Skeleton className="h-5" />
            </TableCell>
            <TableCell>
                <Skeleton className="h-5" />
            </TableCell>
        </TableRow>
    ))
}
